import {StyleSheet, View, Text, Image, TouchableOpacity, useWindowDimensions} from "react-native-web";
import React, {useEffect, useState} from "react";
import dupeLogo from "../icons/Dupe_Logo_Blue.png";
import {acceptBusinessInviteSummary, getBusinessInviteSummary} from "../services/BusinessService";
import {useNavigate, useParams} from "react-router-dom";
import Button from "./Button";
import {clearPendingInviteId, joinPath, loginPathForLocation} from "../utils/inviteRedirect";

const GENERIC_ERROR = "Something went wrong. Please try again.";

function messageForStatus(status, fallback) {
    switch (status) {
        case 404:
        case 410:
            return "This invite has expired or already been used. Ask your team for a new one.";
        case 403:
            return "This invite was sent to a different email address. Sign in with the invited account.";
        case 409:
            return "You're already a member of this Biz.";
        default:
            return fallback;
    }
}

const Join = () => {

    const [inviteSummary, setInviteSummary] = useState({})
    const [errorText, setErrorText] = useState(null)
    const [accepting, setAccepting] = useState(false)
    const { width } = useWindowDimensions();
    let { inviteId } = useParams();
    const navigate = useNavigate();

    // An expired session mid-flow still has to come back here afterwards.
    function redirectToLogin() {
        navigate(loginPathForLocation({pathname: joinPath(inviteId)}));
    }

    function acceptInvite() {
        setAccepting(true);
        setErrorText(null);
        acceptBusinessInviteSummary(inviteId)
            .then(resp => {
                if (resp?.status === 200) {
                    clearPendingInviteId();
                    navigate('/home');
                    return;
                }
                setErrorText(messageForStatus(resp?.status, "We couldn't add you to this Biz."));
            })
            .catch(err => {
                const status = err?.response?.status;
                if (status === 401) {
                    redirectToLogin();
                    return;
                }
                setErrorText(messageForStatus(status, "We couldn't add you to this Biz."));
            })
            .finally(() => setAccepting(false))
    }

    function declineInvite() {
        clearPendingInviteId();
        navigate('/login');
    }

    useEffect(()=> {
        getBusinessInviteSummary(inviteId)
            .then(resp => {
                    if (resp?.status === 200 && resp.data) {
                        setInviteSummary(resp.data);
                }
            })
            .catch(err => {
                // `err.response` is absent for network and CORS failures, and
                // reading through it unguarded threw inside this handler --
                // which is why a failed invite fetch used to fail silently.
                const status = err?.response?.status;
                if (status === 401) {
                    redirectToLogin();
                    return;
                }
                setErrorText(messageForStatus(status, GENERIC_ERROR));
            })
    }, []);
    return(
        <View style={styles.container}>
            {width > 1000 && <View style={{flex: 1, width: "60%", height: "100%"}}>
                <Image
                    source={{uri: "https://dupe-photos.s3.amazonaws.com/Login_Return.jpg"}}
                    style={{width: "100%", height: "100%"}}
                />
            </View>}
            <View style={{alignItems: "center", width: "40%"}}>
                <View style={styles.logoContainer}>
                    <Image source={dupeLogo} style={styles.logo} />
                    <Text style={styles.logoText}>Biz</Text>
                </View>
                <View style={styles.row}>
                    <Image source={"https://d3p3fw3rutb1if.cloudfront.net/business/photos/" + inviteSummary.logoImgId} style={styles.brandLogo} />
                    <Text>{inviteSummary.name}</Text>
                </View>
                <View style={styles.textRow}>
                    <Text>wants you to join their Biz!</Text>
                </View>
                <View style={styles.row}>
                    <Button variant={'secondary'} text={"Decline"} onPress={declineInvite} disabled={accepting} />
                    <Button onPress={acceptInvite} text={accepting ? "Joining..." : "Join"} disabled={accepting} />
                </View>
                {errorText ? <Text style={styles.errorText}>{errorText}</Text> : null}
            </View>
        </View>
    )
}

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: '#f8f7f4',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: "row"
    },
    continueWithEmailBtn:{
        flexDirection: "row",
        width: 350,
        backgroundColor:"#236fea",
        borderRadius:25,
        height:50,
        alignItems:"center",
        justifyContent:"center",
        marginTop: 10,
        marginBottom: 10
    },
    socialBtn:{
        flexDirection: "row",
        width: 350,
        backgroundColor: "white",
        borderRadius:25,
        borderWidth: 1,
        borderStyle: "solid",
        borderColor: "black",
        height:50,
        alignItems:"center",
        justifyContent: "center",
        marginTop: 10,
        marginBottom: 10
    },
    loginLogo:{
        width: 100,
        height: 50
    },
    loginText: {
        color: "white",
        fontSize: 20,
    },
    socialLoginText: {
        color: "black",
        fontSize: 20,
    },
    mailIcon: {
        width: 20,
        height: 20,
        marginRight: 10
    },
    appleIcon: {
        width: 20,
        height: 20,
        marginBottom: 3,
        marginRight: 10
    },
    googleIcon: {
        width: 30,
        height: 30,
        marginRight: 10
    },
    lato: {
        fontFamily: "'Lato', sans-serif"
    },
    latoBold: {
        fontFamily: "'Lato', sans-serif",
        fontWeight: 700
    },
    logoContainer: {
        flexDirection: 'row',
        alignItems: 'flex-end',
        justifyContent: 'center',
        marginBottom: 30,
    },
    logo: {
        width: 120,
        height: 50,
        resizeMode: 'contain',
    },
    logoText: {
        fontSize: 50,
        fontFamily: "'forma-djr-display', sans-serif",
        color: '#396de0',
    },
    brandLogo: {
        width: 50,
        height: 50,
        borderRadius: '50%',
    },
    row: {
        flexDirection: 'row',
        justifyContent: 'center',
        alignItems: 'center',
    },
    textRow: {
        marginTop: 20,
        marginBottom: 40,
    },
    errorText: {
        marginTop: 20,
        maxWidth: 350,
        textAlign: 'center',
        color: '#e03939',
        fontFamily: "'Lato', sans-serif",
        fontSize: 16,
    }
})

export default Join